// Module pour initialiser les modales conformes RGAA
export default function initModals(): void {
    const modalContainer = document.querySelectorAll<HTMLDialogElement>('.js-modal');
    modalContainer.forEach(container => {
        new AccessibleModal(container);
    });
}

/**
 * Classe qui gère le comportement des modales conformes RGAA
 */
class AccessibleModal {
    private body: HTMLElement;
    private el: HTMLDialogElement;
    private previouslyFocusedElement: HTMLElement | null = null;
    private focusableElements: HTMLElement[] = [];

    constructor(el: HTMLDialogElement) {
        this.body = document.body;
        this.el = el;
        this.init();
    }

    private init(): void {
        const modalId = this.el.id;
        const modalOpenButton = this.body.querySelector(`[data-modal-open="${modalId}"]`) as HTMLElement;
        const modalCloseButton = this.el.querySelector(`[data-modal-close="${modalId}"]`) as HTMLElement;

        // Événement pour ouvrir la modale
        modalOpenButton?.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal(modalOpenButton);
        });

        // Événement pour fermer la modale via le bouton
        modalCloseButton?.addEventListener('click', () => {
            this.closeModal();
        });

        // Fermer avec Échap
        this.el.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.closeModal();
            }
        });

        // Piéger le focus dans la modale
        this.el.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
                this.trapFocus(e);
            }
        });

        // Fermer en cliquant sur le backdrop
        this.el.addEventListener('click', (e) => {
            if (e.target === this.el) {
                this.closeModal();
            }
        });
    }

    private openModal(trigger: HTMLElement): void {
        // Stocker l'élément qui a ouvert la modale
        this.previouslyFocusedElement = trigger;

        // Rendre le reste de la page inerte
        this.setPageInert(true);

        // Ouvrir la modale
        this.el.showModal();

        // Mettre à jour les éléments focusables
        this.updateFocusableElements();

        // Focaliser le premier élément focusable ou la modale elle-même
        this.focusFirstElement();

        // Annoncer l'ouverture aux lecteurs d'écran
        this.announceToScreenReader(__('Modale ouverte', 'limpide-starter'));

        // Empêcher le scroll du body
        this.body.style.overflow = 'hidden';

        // Événement personnalisé pour l'ouverture
        const event = new Event('dialogopen');
        this.el.dispatchEvent(event);
    }

    private closeModal(): void {
        this.el.close();

        // Rendre la page accessible à nouveau
        this.setPageInert(false);

        // Restaurer le focus
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }

        // Restaurer le scroll
        this.body.style.overflow = '';

        // Annoncer la fermeture
        this.announceToScreenReader(__('Modale fermée', 'limpide-starter'));
    }

    private updateFocusableElements(): void {
        const focusableSelectors = [
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])',
            'iframe',
            'object',
            'embed',
            '[contenteditable]'
        ].join(', ');

        this.focusableElements = Array.from(
            this.el.querySelectorAll<HTMLElement>(focusableSelectors)
        ).filter(el => {
            // Filtrer les éléments visibles
            const style = window.getComputedStyle(el);
            return style.display !== 'none' && style.visibility !== 'hidden';
        });
    }

    private focusFirstElement(): void {
        // Priorité : bouton de fermeture si pas d'autres éléments focusables
        const closeButton = this.el.querySelector('[data-modal-close]') as HTMLElement;
        
        if (this.focusableElements.length > 0) {
            this.focusableElements[0].focus();
        } else if (closeButton) {
            closeButton.focus();
        } else {
            this.el.focus();
        }
    }

    private trapFocus(e: KeyboardEvent): void {
        if (this.focusableElements.length === 0) return;

        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }

    private setPageInert(inert: boolean): void {
        const mainContent = document.querySelector('main');
        const header = document.querySelector('header');
        const footer = document.querySelector('footer');

        [mainContent, header, footer].forEach(element => {
            if (element) {
                if (inert) {
                    element.setAttribute('inert', '');
                    element.setAttribute('aria-hidden', 'true');
                } else {
                    element.removeAttribute('inert');
                    element.removeAttribute('aria-hidden');
                }
            }
        });
    }

    private announceToScreenReader(message: string): void {
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = message;
        
        document.body.appendChild(announcement);
        
        // Supprimer après annonce
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    }
}

// Fonction utilitaire pour les traductions (à adapter selon votre système)
function __(text: string, domain: string): string {
    // À remplacer par votre système de traduction
    return text;
}